// Pre-upload video transcode (§10.1). Source short-drama episodes ship at high
// bitrates, so we re-encode to 720p (long edge ≤1280, never upscaled), CRF 27,
// and 96k AAC before upload. Frame rate is inherited from the source so callers
// can choose any provider-supported sampling rate without losing frames during
// compression. Best-effort: if ffmpeg is missing or the encode fails, the caller
// uploads the original.

import { spawn } from "node:child_process";
import * as fs from "node:fs";
import * as path from "node:path";

// Episodes already at/below this size upload as-is — transcoding buys little and
// costs CPU/time. Matches the ~20 MB per-episode target.
const SKIP_BELOW_BYTES = 20 * 1024 * 1024;

// Orientation-agnostic: clamp the LONG edge to 1280 (landscape → 720p tall,
// portrait → 1280 tall), keep aspect, and never upscale a smaller source. `-2`
// keeps the other edge even (libx264 requires it).
const SCALE_FILTER =
  "scale='if(gt(a,1),min(1280,iw),-2)':'if(gt(a,1),-2,min(1280,ih))'";

export interface TranscodeResult {
  path: string; // path to upload (transcoded output, or the original on skip/fallback)
  mime: string; // mime to upload with
  transcoded: boolean; // true when we produced a re-encoded file
  note?: string; // human-readable reason when we did NOT transcode
}

function ffmpegArgs(src: string, out: string): string[] {
  return [
    "-y", "-loglevel", "error",
    "-i", src,
    "-vf", SCALE_FILTER,
    "-c:v", "libx264", "-preset", "medium", "-crf", "27", "-pix_fmt", "yuv420p",
    "-c:a", "aac", "-b:a", "96k",
    "-movflags", "+faststart",
    "-f", "mp4",
    out,
  ];
}

function runFfmpeg(src: string, out: string, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new Error("aborted")); return; }
    const child = spawn("ffmpeg", ffmpegArgs(src, out), { stdio: ["ignore", "ignore", "pipe"] });
    let stderr = "";
    child.stderr?.on("data", (d) => { stderr += String(d); });
    const onAbort = (): void => { child.kill("SIGKILL"); };
    signal?.addEventListener("abort", onAbort, { once: true });
    child.on("error", (err) => { signal?.removeEventListener("abort", onAbort); reject(err); });
    child.on("close", (code) => {
      signal?.removeEventListener("abort", onAbort);
      if (code === 0) resolve();
      else reject(new Error(`ffmpeg exited ${code}: ${stderr.trim().slice(0, 300)}`));
    });
  });
}

// Transcode `src` to `out` for upload. Never throws for encode/tooling problems —
// falls back to the original file so a missing ffmpeg or a stubborn clip cannot
// fail the ingest run. `out` is only written when transcoding actually happens.
export async function transcodeForUpload(
  src: string,
  out: string,
  mime: string,
  signal?: AbortSignal,
): Promise<TranscodeResult> {
  // Reuse an already-transcoded output (resume path) rather than re-encoding.
  // Safe because `out` is published atomically (rename below) — it exists ONLY
  // when fully written, so a resume can never reuse a truncated/corrupt encode.
  if (fs.existsSync(out) && (fs.statSync(out).size ?? 0) > 0) {
    return { path: out, mime: "video/mp4", transcoded: true };
  }
  let size = 0;
  try { size = fs.statSync(src).size; } catch { /* stat failure → try transcode anyway */ }
  if (size > 0 && size <= SKIP_BELOW_BYTES) {
    return { path: src, mime, transcoded: false, note: `already ${(size / 1024 / 1024).toFixed(1)}MB (≤20MB)` };
  }
  // Encode to a `.part` sidecar, then atomically rename onto `out`. A SIGKILL
  // mid-encode (the ingest process gets reaped constantly) would otherwise leave a
  // half-written, moov-less file at `out` that the reuse check above happily feeds
  // to Gemini forever → the episode is stuck on "file processing failed" and can
  // never self-heal. With the rename, a killed run leaves only `.part`, which the
  // next attempt overwrites.
  const tmp = `${out}.part`;
  try {
    fs.mkdirSync(path.dirname(out), { recursive: true });
    fs.rmSync(tmp, { force: true }); // drop any leftover from a previously killed encode
    await runFfmpeg(src, tmp, signal);
    if (!fs.existsSync(tmp) || fs.statSync(tmp).size === 0) throw new Error("ffmpeg produced no output");
    fs.renameSync(tmp, out); // atomic publish
    return { path: out, mime: "video/mp4", transcoded: true };
  } catch (err) {
    // Best-effort: clean partial outputs (both the sidecar and any stale target)
    // and upload the original.
    try { fs.rmSync(tmp, { force: true }); fs.rmSync(out, { force: true }); } catch { /* ignore */ }
    if (signal?.aborted) throw err instanceof Error ? err : new Error(String(err));
    const reason = err instanceof Error ? err.message : String(err);
    return { path: src, mime, transcoded: false, note: `transcode failed, uploading original (${reason})` };
  }
}
